import {
  AppWindow,
  ChevronDown,
  ChevronUp,
  UserCog,
  Building2,
  Clapperboard,
  Gauge,
  Home,
  KeyRound,
  LayoutList,
  LogOut,
  Megaphone,
  MonitorSmartphone,
  Music,
  Package,
  Plug,
  Siren,
  Tv,
  Users,
  UtensilsCrossed,
  Waves,
  AlarmClock,
  Info,
  LayoutTemplate,
} from 'lucide-react';
import React, { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { ConfirmHost } from '../components/Confirm';
import { useDeviceLiveUpdates } from './live';
import { useSession } from './session';

function Item({ to, icon, children, soon }: { to: string; icon: ReactNode; children: ReactNode; soon?: boolean }) {
  return (
    <NavLink to={to} end={to === '/'} className={({ isActive }) => `sb-nav-item${isActive ? ' is-active' : ''}${soon ? ' disabled' : ''}`}>
      <span className="sb-nav-icon">{icon}</span>
      <span className="sb-nav-text">{children}</span>
      {soon && <span className="soon">later</span>}
    </NavLink>
  );
}

/** Closes a popover on a click outside [ref] or Escape. */
function useDismiss(open: boolean, ref: React.RefObject<HTMLElement | null>, close: () => void) {
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, ref, close]);
}

/** Site switcher (the PulsePanels workspace box). */
function SiteSwitcher() {
  const { sites, site, setSiteId } = useSession();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(open, ref, () => setOpen(false));
  if (!sites.length) return <div className="sb-workspace-box"><div className="sb-workspace-label">Site</div><div className="sb-workspace-name">No site yet</div></div>;
  return (
    <div className="sb-workspace-dropdown" ref={ref}>
      <button type="button" className="sb-workspace-trigger" onClick={() => setOpen((o) => !o)} aria-expanded={open} disabled={sites.length < 2}>
        <span className="sb-workspace-trigger-text">
          <span className="sb-workspace-label">Site</span>
          <span className="sb-workspace-name">{site?.name}</span>
        </span>
        {sites.length > 1 && <ChevronDown size={15} className={`sb-workspace-caret${open ? ' is-open' : ''}`} />}
      </button>
      {open && (
        <div className="sb-workspace-menu">
          {sites.map((x) => (
            <button type="button" key={x.id} className={`sb-workspace-item${x.id === site?.id ? ' active' : ''}`} onClick={() => { setSiteId(x.id); setOpen(false); }}>
              {x.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Signed-in user at the bottom of the sidebar; opens Account settings and Sign out. */
function UserMenu() {
  const { me, logout } = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(open, ref, () => setOpen(false));
  const initials = (me.user.name || me.user.email).split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('');
  return (
    <div className="sb-user-wrap" ref={ref}>
      {open && (
        <div className="sb-user-menu" role="menu">
          <button type="button" className="sb-user-menu-item" role="menuitem" onClick={() => { setOpen(false); navigate('/account'); }}>
            <span className="sb-user-menu-icon"><UserCog size={18} /></span> Account settings
          </button>
          <button type="button" className="sb-user-menu-item is-danger" role="menuitem" onClick={() => void logout()}>
            <span className="sb-user-menu-icon"><LogOut size={18} /></span> Sign out
          </button>
        </div>
      )}
      <button type="button" className="sb-user-button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="menu">
        <span className="sb-user-avatar">{initials || '?'}</span>
        <span className="sb-user-meta">
          <span className="sb-user-name">{me.user.name || me.user.email}</span>
          <span className="sb-user-email">{me.organization.name} · {me.role.toLowerCase()}</span>
        </span>
        <ChevronUp size={16} className={`sb-user-caret${open ? '' : ' is-closed'}`} />
      </button>
    </div>
  );
}

/** Admin menu from the brief (§47), in the PulsePanels sidebar. */
export function Layout() {
  useDeviceLiveUpdates();
  const s = 18;
  return (
    <div className="app">
      <aside className="sb-shell">
        <div className="sb-top">
          <NavLink to="/" className="sb-brand">
            <img className="sb-brand-logo" src="/pulseiptv-logo-light.png" alt="PulseIPTV" />
          </NavLink>
          <SiteSwitcher />
        </div>
        <nav className="sb-nav">
          <Item to="/" icon={<Gauge size={s} />}>Dashboard</Item>

          <div className="sb-nav-group">Content</div>
          <Item to="/content/live-tv" icon={<Tv size={s} />}>Live TV</Item>
          <Item to="/content/packages" icon={<Package size={s} />}>Packages</Item>
          <Item to="/content/vod" icon={<Clapperboard size={s} />}>VOD</Item>
          <Item to="/content/aod" icon={<Music size={s} />}>AOD</Item>

          <div className="sb-nav-group">Guest Experience</div>
          <Item to="/experience/tv-menu" icon={<LayoutList size={s} />}>TV Menu</Item>
          <Item to="/experience/welcome" icon={<Home size={s} />}>Welcome &amp; Branding</Item>
          <Item to="/experience/home-design" icon={<LayoutTemplate size={s} />}>Home screen designer</Item>
          <Item to="/experience/facilities" icon={<Waves size={s} />}>Facilities</Item>
          <Item to="/experience/restaurants" icon={<UtensilsCrossed size={s} />}>Restaurants</Item>
          <Item to="/experience/info" icon={<Info size={s} />}>Hotel Information</Item>
          <Item to="/experience/apps" icon={<AppWindow size={s} />}>Apps</Item>
          <Item to="/wake-up-calls" icon={<AlarmClock size={s} />}>Wake-up calls</Item>

          <div className="sb-nav-group">Operations</div>
          <Item to="/sites" icon={<Building2 size={s} />}>Sites &amp; Rooms</Item>
          <Item to="/devices" icon={<MonitorSmartphone size={s} />}>Devices</Item>
          <Item to="/marketing" icon={<Megaphone size={s} />}>Marketing</Item>
          <Item to="/guests" icon={<Users size={s} />}>Guests</Item>
          <Item to="/emergency" icon={<Siren size={s} />}>Emergency</Item>
          <Item to="/integrations" icon={<Plug size={s} />}>Integrations</Item>
          <Item to="/settings" icon={<KeyRound size={s} />}>Settings</Item>
        </nav>
        <div className="sb-bottom">
          <UserMenu />
        </div>
      </aside>
      <div className="main">
        <main className="content">
          <Outlet />
        </main>
        <ConfirmHost />
      </div>
    </div>
  );
}

export function NeedsSite({ children }: { children: ReactNode }) {
  const { site } = useSession();
  if (!site) {
    return (
      <div className="card empty">
        Create your first site in <a href="/sites">Sites &amp; Rooms</a> to continue.
      </div>
    );
  }
  return <>{children}</>;
}
